/**
 * Tile layers (03 §6, §10; 09 §6, §12).
 *
 * - `TileCache` (one per grade, shared by every `TileLayer` of that grade): fetch + decode with
 *   `createImageBitmap(blob, {imageOrientation: 'flipY', premultiplyAlpha: 'none',
 *   colorSpaceConversion: 'none'})` so texel row 0 = south edge. Tiles live as layers of RGBA8
 *   2D texture arrays ("pages" of `pageLayers` layers — 64 by default; the player's day cache uses
 *   16, H1: 4× cheaper all-page mip regeneration, identical pixels — NEAREST, no mips; texelFetch
 *   ignores filtering), one LRU slot = one array layer (texSubImage3D upload). Capacity: an
 *   explicit slot count, or the H1 (L11) rule `min(256, max(96, 2 × wanted))`, applied grow-only
 *   in `pump()` (a resize never drops resident tiles).
 *   LRU rules (no thrash): `want()` marks READY tiles used; a load only takes a free slot or evicts
 *   a tile **not wanted this frame** (oldest first); a visible/base load may also evict a wanted
 *   prefetch tile; a prefetch load never evicts a wanted tile. Loads no longer wanted are aborted.
 *   `pump()` runs once per frame, after every layer's `collect()`.
 *   Retry (audit 2026-10-20 finding 3): an `ERROR` entry is not dead — the next `want()` for it
 *   re-queues it once its cooldown has elapsed (`tileRetryBackoffMs`: 5 s after the first failure,
 *   then 10 s, 20 s, read on the injected clock, default `performance.now()`), and
 *   `TILE_MAX_ATTEMPTS` fetches total makes the entry terminal for this cache residency — a tile
 *   that pans out of view and back gets a fresh attempt budget (`pump()` purges unwanted queued
 *   entries, dropping attempts; no re-queue while resident). A
 *   deterministic failure is terminal at once: the decoded-size check of finding 13 (a bitmap that
 *   is not `tile_px`² never reaches `upload`, and `bmp.close()` runs on every decode→upload path).
 *   Sampling (03 §10 gap 5): a cache is built `nearest` (default) or `linear`. Linear pages get a
 *   mip chain (TRILINEAR samplers) and re-run `generateMipmap` on the page after each tile upload,
 *   so a page is never sampled with an incomplete chain (uploads complete between frames, before
 *   the next `pump()`/draw; generation is all-layers, one page — acceptable while streaming).
 * - `TileLayer` (one per camera): level by **backbuffer** mpp (stage mpp / uPxScale, 09 §6) or
 *   stage mpp (`levelBy: 'stage'`), visible tiles of the rotated ortho view plus a one-tile
 *   prefetch ring, parent-level fallback while loading (z0 is always wanted as the last-resort
 *   base). The cache is first asked for the capacity the UNBIASED set needs (HPF2); if even the
 *   ceiling cannot hold it, the layer drops the prefetch ring and then picks coarser levels
 *   (automatic level bias) instead of thrashing. Drawing is instanced: one
 *   thin-instanced quad mesh per texture page (instance = (x0, y0, tile_m, layer)), so a camera
 *   costs at most `capacity / 64` tile draws.
 */
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import '@babylonjs/core/Meshes/thinInstanceMesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { BaseTexture } from '@babylonjs/core/Materials/Textures/baseTexture';
import { Constants } from '@babylonjs/core/Engines/constants';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Engine } from '@babylonjs/core/Engines/engine';
import type { AbstractEngine } from '@babylonjs/core/Engines/abstractEngine';
import type { Scene } from '@babylonjs/core/scene';
import type { Observer } from '@babylonjs/core/Misc/observable';
import { GROUP, HUB_UNIFORMS, LAYER, type UniformHub } from './engine.ts';
import { rectCornersWorld } from './projection.ts';
import { TILE_VERTEX, tileFragment } from './shaders/tile.ts';
import {
  TileKeySet,
  ancestorKey,
  keyX,
  keyY,
  keyZ,
  levelForView,
  tileUrl,
  visibleTiles,
  type TileGridSpec,
} from './tileGrid.ts';
import type { MapView } from './view.ts';

// ───────────────────────────── grade hook ─────────────────────────────

/** A style's fragment grade hook (03 §10): GLSL defining `vec4 ucGrade(vec2 uv, vec2 world)`. */
export interface GradeHook {
  glsl: string;
  /** extra uniforms the hook declares */
  uniforms?: readonly string[];
  /** extra samplers (LUTs) the hook declares */
  samplers?: readonly string[];
  defines?: readonly string[];
  /** set the hook's uniforms/samplers on a new material */
  setup?(mat: ShaderMaterial): void;
}

/** M1 default: coverage-weighted luminance (R) → 16-step grey ramp with the stage-px Bayer. */
export const DEFAULT_GRADE: GradeHook = {
  glsl: /* glsl */ `
uniform vec3 uGradeBg;
vec4 ucGrade(vec2 uv, vec2 world) {
  vec4 s = ucTexelFiltered(uv, vec3(1.0, 0.0, 1.0));
  if (s.a <= 0.5) return vec4(uGradeBg, 1.0);
  float q = clamp(floor(s.r * 15.0 + ucBayer4(ucStageCell())), 0.0, 15.0) / 15.0;
  return vec4(vec3(0.03 + 0.85 * q), 1.0);
}
`,
  uniforms: ['uGradeBg'],
  setup(mat) {
    mat.setColor3('uGradeBg', new Color3(5 / 255, 7 / 255, 14 / 255));
  },
};

// ───────────────────────────── cache ─────────────────────────────

/** Texture-array sampling mode (03 §10 gap 5): `nearest` = texelFetch, no mips (unchanged default);
 *  `linear` = mipmapped pages + hardware trilinear (shader `GLSL_TILE_SAMPLING_LINEAR`). */
export type TileSampling = 'nearest' | 'linear';

const enum TS {
  QUEUED = 1,
  LOADING = 2,
  READY = 3,
  ERROR = 4,
}

/** Priorities: visible 0, fallback/base 1, prefetch 2. */
export const PRIO_VISIBLE = 0;
export const PRIO_BASE = 1;
export const PRIO_PREFETCH = 2;

interface TileEntry {
  key: number;
  state: TS;
  /** global slot (page · pageLayers + layer), −1 = none */
  slot: number;
  lastUsed: number;
  wantFrame: number;
  prio: number;
  ctrl: AbortController | null;
  aborted: boolean;
  /** failed fetches so far (finding 3); terminal at `TILE_MAX_ATTEMPTS` */
  attempts: number;
  /** earliest clock time (ms) the next `want()` may re-queue an ERROR entry */
  retryAt: number;
  /** terminal for this cache residency (attempt cap, or a deterministic failure) — a tile that
   *  pans out of view and back gets a fresh attempt budget: pump() purges unwanted queued
   *  entries, dropping attempts */
  terminal: boolean;
}

/**
 * Finding 3 (audit 2026-10-20): a failed tile re-queues on a doubling, clock-based cooldown —
 * 5 s after the first failure, then 10 s, 20 s (`tileRetryBackoffMs`). `TILE_MAX_ATTEMPTS` fetches
 * total, so the third consecutive failure is terminal for the session and the 20 s rung is only
 * reached if that cap is ever raised.
 */
export const TILE_RETRY_BASE_MS = 5000;
/** finding 3: fetches per tile key before the entry goes terminal for the session */
export const TILE_MAX_ATTEMPTS = 3;

/** The cooldown (ms) after the n-th consecutive failure of one tile (n is 1-based): 5 s, 10 s, 20 s. */
export function tileRetryBackoffMs(attempt: number): number {
  return TILE_RETRY_BASE_MS * 2 ** Math.max(0, attempt - 1);
}

export interface TileCacheOptions {
  /** slots (texture-array layers); default: the H1 (L11) rule, applied grow-only from 96 */
  capacity?: number;
  /** layers per texture page (one draw per page), default 64 */
  pageLayers?: number;
  concurrency?: number;
  /** page sampling mode (default 'nearest'; 'linear' builds mipmapped TRILINEAR pages) */
  sampling?: TileSampling;
  /** override fetch (tests) */
  fetchFn?: typeof fetch;
  /** tile base URL if different from baseUrl */
  tilesBase?: string | undefined;
  /** callback for tile load failures */
  onTileError?: ((err: unknown) => void) | undefined;
  /** wall clock in ms for the retry cooldown (finding 3); default `performance.now()` (tests inject) */
  clock?: () => number;
}

export interface TileCacheStats {
  ready: number;
  loading: number;
  queued: number;
  errors: number;
  evicted: number;
  fetches: number;
  pages: number;
}

interface Page {
  tex: BaseTexture;
}

// ───────────────────────────── capacity (H1, L11) ─────────────────────────────

/** capacity floor: the 1080p fitted view plus rings, base and inset, with slack */
export const TILE_CAPACITY_MIN = 96;
/** capacity ceiling: the old fixed size; a 4K session saturates it */
export const TILE_CAPACITY_MAX = 256;

/** H1 (L11): `capacity = min(256, max(96, 2 × wanted))` — twice what the layers want this frame
 *  (main + inset visible sets, rings, base), so steady state never evicts, floored at 96 slots
 *  (≈ 128 MiB GPU with mips) and capped at 256. The 1080p fitted view lands at 172; 4K saturates
 *  the cap (measured, see the r14 report). */
export function tileCacheCapacity(wanted: number): number {
  return Math.min(TILE_CAPACITY_MAX, Math.max(TILE_CAPACITY_MIN, 2 * wanted));
}

export class TileCache {
  /** current slot count (a rule-based cache only grows, so slots stay valid) */
  capacity: number;
  readonly pageLayers: number;
  /** pages the CURRENT capacity spans (grows with it); meshes exist for `maxPageCount` */
  pageCount: number;
  /** the capacity ceiling: the explicit option, or the H1 cap for rule-based caches */
  readonly maxCapacity: number;
  /** pages the ceiling spans (layers build one mesh per page up front) */
  readonly maxPageCount: number;
  readonly sampling: TileSampling;
  readonly baseUrl: string;
  readonly tilesBase: string;
  private readonly concurrency: number;
  private readonly entries = new Map<number, TileEntry>();
  private readonly ready: TileEntry[] = [];
  private readonly queue: TileEntry[] = [];
  private readonly loading: TileEntry[] = [];
  private readonly free: number[] = [];
  private readonly pages: (Page | null)[];
  private inflight = 0;
  private frame = 0;
  private disposed = false;
  private lost = false;
  private evicted = 0;
  private fetches = 0;
  /** distinct keys wanted this frame (the H1 capacity rule's input) */
  private wantedFrame = -1;
  private wantedCount = 0;
  /** bumped whenever a tile becomes ready or is evicted (layers rebuild their instances) */
  version = 0;
  /** bumped whenever the capacity grew (layers re-derive their wanted sets) */
  capVersion = 0;
  private readonly fetchFn: typeof fetch;
  /** finding 3: the retry cooldown clock (ms), never the frame counter */
  private readonly clock: () => number;
  private ctxObs: [Observer<AbstractEngine> | null, Observer<AbstractEngine> | null] = [null, null];

  constructor(
    private readonly engine: Engine,
    readonly grid: TileGridSpec,
    baseUrl: string,
    readonly grade: string,
    private readonly opts: TileCacheOptions = {},
  ) {
    this.baseUrl = baseUrl;
    this.tilesBase = opts.tilesBase ?? baseUrl;
    this.maxCapacity = opts.capacity ?? TILE_CAPACITY_MAX;
    this.capacity = opts.capacity ?? TILE_CAPACITY_MIN;
    this.pageLayers = Math.min(opts.pageLayers ?? 64, this.capacity);
    this.pageCount = Math.ceil(this.capacity / this.pageLayers);
    this.maxPageCount = Math.ceil(this.maxCapacity / this.pageLayers);
    this.sampling = opts.sampling ?? 'nearest';
    this.pages = new Array<Page | null>(this.pageCount).fill(null);
    this.concurrency = opts.concurrency ?? 6;
    this.fetchFn = opts.fetchFn ?? fetch.bind(globalThis);
    this.clock = opts.clock ?? (() => performance.now());
    this.resetSlots();
  }

  private resetSlots(): void {
    this.free.length = 0;
    for (let i = this.capacity - 1; i >= 0; i--) this.free.push(i); // pop() hands out slot 0 first
  }

  /** H1 (L11): raise the capacity for `wanted` tiles (the rule, clamped to `maxCapacity`;
   *  never lowers — a resize must not drop resident tiles). Returns true when it grew. */
  ensureCapacity(wanted: number): boolean {
    const cap = Math.min(this.maxCapacity, tileCacheCapacity(wanted));
    if (cap <= this.capacity) return false;
    const from = this.capacity;
    this.capacity = cap;
    const pc = Math.ceil(cap / this.pageLayers);
    for (let p = this.pageCount; p < pc; p++) this.pages.push(null);
    this.pageCount = pc;
    for (let i = cap - 1; i >= from; i--) this.free.push(i);
    this.capVersion++;
    return true;
  }

  /** Subscribe to context loss/restore (abort in flight; on restore drop every texture and entry). */
  attachContextEvents(): void {
    const e = this.engine;
    this.ctxObs = [
      e.onContextLostObservable.add(() => {
        this.lost = true;
        for (const en of this.loading) {
          en.aborted = true;
          en.ctrl?.abort();
        }
      }),
      e.onContextRestoredObservable.add(() => {
        this.lost = false;
        this.clearAll();
      }),
    ];
  }

  /** Drop every texture and entry (context restored, grade reload). */
  clearAll(): void {
    for (const en of this.loading) {
      en.aborted = true;
      en.ctrl?.abort();
    }
    for (let i = 0; i < this.pages.length; i++) {
      this.pages[i]?.tex.dispose();
      this.pages[i] = null;
    }
    this.entries.clear();
    this.ready.length = 0;
    this.queue.length = 0;
    this.loading.length = 0;
    this.inflight = 0;
    this.resetSlots();
    this.version++;
  }

  /** Mark a tile wanted this frame (lower prio = sooner). READY tiles are marked used (LRU). */
  want(key: number, prio: number, frame: number): void {
    this.frame = frame;
    if (this.wantedFrame !== frame) {
      this.wantedFrame = frame;
      this.wantedCount = 0;
    }
    let e = this.entries.get(key);
    if (!e) {
      e = { key, state: TS.QUEUED, slot: -1, lastUsed: frame, wantFrame: frame, prio, ctrl: null, aborted: false, attempts: 0, retryAt: 0, terminal: false };
      this.entries.set(key, e);
      this.queue.push(e);
      this.wantedCount++;
      return;
    }
    if (e.wantFrame !== frame) {
      this.wantedCount++;
      if (e.wantFrame !== Number.MAX_SAFE_INTEGER) e.wantFrame = frame;
      e.prio = prio;
    } else if (prio < e.prio) e.prio = prio;
    if (e.state === TS.READY) e.lastUsed = frame;
    else if (e.state === TS.ERROR) this.maybeRequeue(e);
  }

  /** Finding 3: a failed tile is not dead — re-queue it once its cooldown elapsed. Terminal entries
   *  (the attempt cap, or a deterministic failure set at the throw site) stay failed. */
  private maybeRequeue(e: TileEntry): void {
    if (e.terminal || this.clock() < e.retryAt) return;
    e.state = TS.QUEUED;
    this.queue.push(e);
  }

  /** True if the tile is ready (marks it used this frame). */
  get(key: number, frame: number): boolean {
    const e = this.entries.get(key);
    if (e?.state !== TS.READY) return false;
    e.lastUsed = frame;
    return true;
  }

  /** Global slot of a ready tile, or −1. */
  slotOf(key: number): number {
    const e = this.entries.get(key);
    return e?.state === TS.READY ? e.slot : -1;
  }

  isReady(key: number): boolean {
    return this.entries.get(key)?.state === TS.READY;
  }

  /** The texture-array page for a slot's page index (null until a tile was uploaded to it). */
  pageTexture(page: number): BaseTexture | null {
    return this.pages[page]?.tex ?? null;
  }

  /** Drop stale work and start loads. Call once per frame after every layer's `collect()`. */
  pump(frame: number): void {
    this.frame = frame;
    if (this.lost || this.disposed) return;
    // H1 (L11): grow to fit this frame's wanted set before loading, so a bigger backbuffer never
    // evicts or re-biases for more than the one frame before `capVersion` triggers a re-derive.
    this.ensureCapacity(this.wantedCount);
    for (let i = this.queue.length - 1; i >= 0; i--) {
      const e = this.queue[i];
      if (e && e.wantFrame < frame) {
        this.queue.splice(i, 1);
        this.entries.delete(e.key);
      }
    }
    for (let i = 0; i < this.loading.length; i++) {
      const e = this.loading[i];
      if (e && e.wantFrame < frame && !e.aborted) {
        e.aborted = true;
        e.ctrl?.abort();
      }
    }
    while (this.inflight < this.concurrency && this.queue.length > 0) {
      let bi = 0;
      for (let i = 1; i < this.queue.length; i++) {
        if ((this.queue[i]?.prio ?? 0) < (this.queue[bi]?.prio ?? 0)) bi = i;
      }
      const e = this.queue[bi];
      if (!e) break;
      const slot = this.acquireSlot(frame, e.prio < PRIO_PREFETCH, false);
      if (slot < 0) break; // the best candidate cannot get a slot: nothing of equal or lower priority can
      this.queue.splice(bi, 1);
      e.slot = slot;
      void this.load(e);
    }
  }

  /**
   * A free slot, else evict the least recently used READY tile not wanted this frame; with
   * `allowPrefetch`, else a READY tile wanted only as prefetch; with `any`, any READY tile.
   */
  private acquireSlot(frame: number, allowPrefetch: boolean, any: boolean): number {
    const f = this.free.pop();
    if (f !== undefined) return f;
    let bi = -1;
    let best = Infinity;
    for (let pass = 0; pass < 3 && bi < 0; pass++) {
      if (pass === 1 && !allowPrefetch) continue;
      if (pass === 2 && !any) continue;
      for (let i = 0; i < this.ready.length; i++) {
        const e = this.ready[i];
        if (!e) continue;
        const ok = pass === 0 ? e.wantFrame < frame : pass === 1 ? e.prio >= PRIO_PREFETCH && e.wantFrame !== Number.MAX_SAFE_INTEGER : true;
        if (ok && e.lastUsed < best) {
          best = e.lastUsed;
          bi = i;
        }
      }
    }
    if (bi < 0) return -1;
    const v = this.ready[bi];
    if (!v) return -1;
    this.ready.splice(bi, 1);
    this.entries.delete(v.key);
    this.evicted++;
    this.version++;
    return v.slot;
  }

  private releaseSlot(e: TileEntry): void {
    if (e.slot >= 0) this.free.push(e.slot);
    e.slot = -1;
  }

  private async load(e: TileEntry): Promise<void> {
    e.state = TS.LOADING;
    e.aborted = false;
    this.inflight++;
    this.fetches++;
    this.loading.push(e);
    const ctrl = typeof AbortController === 'undefined' ? null : new AbortController();
    e.ctrl = ctrl;
    const url = tileUrl(this.grid, this.tilesBase, this.grade, keyZ(e.key), keyX(e.key), keyY(e.key));
    const stale = (): boolean => e.aborted || this.disposed || this.lost; // re-read after awaits
    try {
      const res = await this.fetchFn(url, ctrl ? { signal: ctrl.signal } : {});
      if (!res.ok) throw new Error(`tile ${url}: HTTP ${String(res.status)}`);
      const blob = await res.blob();
      const bmp = await createImageBitmap(blob, {
        imageOrientation: 'flipY',
        premultiplyAlpha: 'none',
        colorSpaceConversion: 'none',
      });
      // Finding 13 (audit 2026-10-20): the bitmap is closed on EVERY path out of decode→upload
      // (`upload()` may throw), and a decode that is not `tile_px`² is deterministic — the slot is
      // released by the shared error path and the entry goes terminal (a re-download cannot help).
      try {
        if (stale()) throw new Error('aborted');
        const n = this.grid.tile_px;
        if (bmp.width !== n || bmp.height !== n) {
          e.terminal = true;
          throw new Error(`tile ${url}: decoded ${String(bmp.width)}×${String(bmp.height)}, expected ${String(n)}×${String(n)}`);
        }
        this.upload(bmp, e.slot);
      } finally {
        bmp.close();
      }
      e.state = TS.READY;
      e.lastUsed = this.frame;
      this.ready.push(e);
      this.version++;
    } catch (err) {
      this.releaseSlot(e);
      if (stale()) {
        if (this.entries.get(e.key) === e) this.entries.delete(e.key);
      } else {
        e.state = TS.ERROR;
        e.attempts++;
        // `e.terminal` may already be set by the failure site (a deterministic error, finding 13);
        // otherwise the attempt cap is what ends the retries.
        if (e.terminal || e.attempts >= TILE_MAX_ATTEMPTS) e.terminal = true;
        else e.retryAt = this.clock() + tileRetryBackoffMs(e.attempts);
        console.warn(String(err));
        this.opts.onTileError?.(err);
      }
    } finally {
      e.ctrl = null;
      this.inflight--;
      const li = this.loading.indexOf(e);
      if (li >= 0) this.loading.splice(li, 1);
    }
  }

  private page(p: number): Page {
    let pg = this.pages[p];
    if (!pg) {
      const n = this.grid.tile_px;
      const linear = this.sampling === 'linear';
      const it = this.engine.createRawTexture2DArray(
        null,
        n,
        n,
        Math.min(this.pageLayers, this.capacity - p * this.pageLayers),
        Constants.TEXTUREFORMAT_RGBA,
        linear,
        false,
        linear ? Constants.TEXTURE_TRILINEAR_SAMPLINGMODE : Constants.TEXTURE_NEAREST_SAMPLINGMODE,
      );
      it._bufferView = null;
      pg = { tex: new BaseTexture(this.engine, it) };
      this.pages[p] = pg;
    }
    return pg;
  }

  /** Upload an ImageBitmap into a slot's array layer (no flip, no premultiply at upload). */
  private upload(bmp: ImageBitmap, slot: number): void {
    const p = Math.floor(slot / this.pageLayers);
    const layer = slot % this.pageLayers;
    const it = this.page(p).tex.getInternalTexture();
    if (!it) return;
    const eng = this.engine;
    const gl = eng._gl;
    eng._bindTextureDirectly(gl.TEXTURE_2D_ARRAY, it, true);
    eng._unpackFlipY(false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texSubImage3D(gl.TEXTURE_2D_ARRAY, 0, 0, 0, layer, bmp.width, bmp.height, 1, gl.RGBA, gl.UNSIGNED_BYTE, bmp);
    if (this.sampling === 'linear') {
      // refresh the whole page's mip chain (WebGL2 has no per-layer generateMipmap). Immediate, so
      // the page is never bound with an incomplete chain at the next draw.
      gl.generateMipmap(gl.TEXTURE_2D_ARRAY);
    }
    eng._bindTextureDirectly(gl.TEXTURE_2D_ARRAY, null);
  }

  stats(): TileCacheStats {
    let errors = 0;
    for (const e of this.entries.values()) if (e.state === TS.ERROR) errors++;
    let pages = 0;
    for (const p of this.pages) if (p) pages++;
    return { ready: this.ready.length, loading: this.inflight, queued: this.queue.length, errors, evicted: this.evicted, fetches: this.fetches, pages };
  }

  dispose(): void {
    this.disposed = true;
    this.clearAll();
    const [a, b] = this.ctxObs;
    if (a) this.engine.onContextLostObservable.remove(a);
    if (b) this.engine.onContextRestoredObservable.remove(b);
  }
}

// ───────────────────────────── layer ─────────────────────────────

export interface TileHost {
  scene: Scene;
  engine: Engine;
  hub: UniformHub;
  /** backbuffer px per stage px (uPxScale = k / r): tile levels follow the backbuffer mpp */
  pxScale(): number;
}

export interface TileLayerOptions {
  /** the camera's view (MapCamera, or an inset's `view`) */
  camera: MapView;
  /** 'backbuffer' (default): level by stage mpp / uPxScale; 'stage': by stage mpp */
  levelBy?: 'backbuffer' | 'stage';
  gradeHook?: GradeHook;
  layerMask?: number;
  /** > 0 picks coarser levels */
  levelBias?: number;
  /** prefetch ring in tiles (default 1) */
  ring?: number;
  renderingGroupId?: number;
  /** stage-cell-centre sampling; default: on with levelBy 'stage' */
  cellSampling?: boolean;
  /** slots kept free for other layers sharing the cache (default 16) */
  reserve?: number;
  /** 'nearest' (default: the cache's sampling) or 'linear' — hardware-filtered mip path
   *  (03 §10 gap 5). Physical to the cache's pages: an explicit value must match
   *  `cache.sampling`, or the constructor throws. */
  sampling?: TileSampling;
}

interface PageMesh {
  mesh: Mesh;
  data: Float32Array;
  n: number;
  page: number;
}

export interface TileLayerStats {
  level: number;
  /** automatic level bias applied because the visible set did not fit the cache */
  autoBias: number;
  visible: number;
  prefetch: number;
  /** tiles drawn (instances) */
  drawn: number;
  fallback: number;
  /** draw calls (page meshes with instances) */
  draws: number;
}

let layerSeq = 0;

export class TileLayer {
  private readonly view: MapView;
  private readonly vis = new TileKeySet(2048);
  private readonly pre = new TileKeySet(2048);
  private readonly base = new TileKeySet(16);
  private readonly fallback = new TileKeySet(2048);
  private readonly quad = new Float64Array(8);
  private readonly meshes: PageMesh[] = [];
  private level = 0;
  private autoBias = 0;
  private drawn = 0;
  private lastViewVersion = -1;
  private lastCacheVersion = -1;
  private lastCapVersion = -1;
  private needsDraw = true;
  private readonly lastRect = { x: NaN, y: NaN, w: NaN, h: NaN, s: NaN };
  private material: ShaderMaterial;
  private bindObs: Observer<AbstractMesh> | null = null;
  private enabled = true;
  private readonly id = layerSeq++;
  private readonly opts: Required<Omit<TileLayerOptions, 'camera' | 'gradeHook'>> & { gradeHook: GradeHook };
  /** resolved sampling mode ('linear' layers compile the hardware-filtered mip path) */
  readonly sampling: TileSampling;

  constructor(
    private readonly host: TileHost,
    readonly cache: TileCache,
    o: TileLayerOptions,
  ) {
    this.view = o.camera;
    const levelBy = o.levelBy ?? 'backbuffer';
    this.sampling = o.sampling ?? cache.sampling;
    if (o.sampling !== undefined && o.sampling !== cache.sampling) {
      throw new Error(`TileLayer: sampling '${o.sampling}' but the cache serves '${cache.sampling}' pages`);
    }
    this.opts = {
      levelBy,
      gradeHook: o.gradeHook ?? DEFAULT_GRADE,
      layerMask: o.layerMask ?? LAYER.MAIN,
      levelBias: o.levelBias ?? 0,
      ring: o.ring ?? 1,
      renderingGroupId: o.renderingGroupId ?? GROUP.TILES,
      cellSampling: o.cellSampling ?? levelBy === 'stage',
      reserve: o.reserve ?? 16,
      sampling: this.sampling,
    };
    this.material = this.buildMaterial(this.opts.gradeHook);
    // one mesh per page of the cache's CEILING: a rule-based cache grows its capacity (H1) and
    // with it the reachable pages; unused page meshes stay hidden until a tile lands in them
    for (let p = 0; p < cache.maxPageCount; p++) this.meshes.push(this.buildMesh(p));
  }

  private buildMesh(page: number): PageMesh {
    const mesh = new Mesh(`tiles${String(this.id)}-p${String(page)}`, this.host.scene);
    const vd = new VertexData();
    vd.positions = [0, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1];
    vd.indices = [0, 2, 1, 0, 3, 2];
    vd.applyToMesh(mesh, false);
    mesh.isPickable = false;
    mesh.alwaysSelectAsActiveMesh = true;
    mesh.doNotSyncBoundingInfo = true;
    mesh.layerMask = this.opts.layerMask;
    mesh.renderingGroupId = this.opts.renderingGroupId;
    const data = new Float32Array(this.cache.pageLayers * 16);
    mesh.thinInstanceSetBuffer('matrix', data, 16, false);
    mesh.thinInstanceCount = 0;
    mesh.refreshBoundingInfo();
    mesh.material = this.material;
    mesh.isVisible = false;
    const pm: PageMesh = { mesh, data, n: 0, page };
    mesh.metadata = pm;
    return pm;
  }

  private buildMaterial(hook: GradeHook): ShaderMaterial {
    const defines = [...(hook.defines ?? [])];
    if (this.opts.cellSampling) defines.push('UC_CELL_SAMPLING');
    const frag = tileFragment(hook.glsl, this.opts.sampling === 'linear');
    const mat = new ShaderMaterial(
      `tiles${String(this.id)}`,
      this.host.scene,
      { vertexSource: TILE_VERTEX, fragmentSource: frag },
      {
        attributes: ['position'],
        uniforms: ['viewProjection', 'uMinTileM', ...HUB_UNIFORMS, ...(hook.uniforms ?? [])],
        samplers: ['uTiles', ...(hook.samplers ?? [])],
        defines,
      },
    );
    mat.backFaceCulling = false;
    let minTile = Infinity;
    for (const l of this.cache.grid.levels) minTile = Math.min(minTile, l.tile_m);
    mat.setFloat('uMinTileM', minTile);
    this.host.hub.register(mat);
    hook.setup?.(mat);
    this.bindObs = mat.onBindObservable.add((mesh) => {
      const pm = mesh.metadata as PageMesh | null;
      if (!pm) return;
      const tex = this.cache.pageTexture(pm.page);
      if (tex) mat.getEffect().setTexture('uTiles', tex);
    });
    for (const m of this.meshes) m.mesh.material = mat;
    return mat;
  }

  /** Swap the grade hook (rebuilds the material). */
  setGradeHook(hook: GradeHook): void {
    const old = this.material;
    if (this.bindObs) old.onBindObservable.remove(this.bindObs);
    this.opts.gradeHook = hook;
    this.material = this.buildMaterial(hook);
    old.dispose();
  }

  get shaderMaterial(): ShaderMaterial {
    return this.material;
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (!on) for (const m of this.meshes) m.mesh.isVisible = false;
    else {
      this.lastViewVersion = -1;
      this.needsDraw = true;
    }
  }

  /** Stand-alone per-frame step: collect + pump + draw (GlCore splits these across all layers). */
  update(): void {
    const frame = this.host.engine.frameId;
    this.collect(frame);
    this.cache.pump(frame);
    this.draw(frame);
  }

  /** Phase 1: recompute the tile sets when the view changed; mark every needed tile wanted. */
  collect(frame: number): void {
    if (!this.enabled) return;
    const v = this.view;
    const r = v.rect;
    const s = this.host.pxScale();
    const lr = this.lastRect;
    const c = this.cache;
    // a capacity growth (H1) re-derives the wanted sets too: the budget decision must be re-taken
    if (v.version !== this.lastViewVersion || lr.x !== r.x || lr.y !== r.y || lr.w !== r.w || lr.h !== r.h || lr.s !== s || c.capVersion !== this.lastCapVersion) {
      this.lastViewVersion = v.version;
      this.lastCapVersion = c.capVersion;
      lr.x = r.x;
      lr.y = r.y;
      lr.w = r.w;
      lr.h = r.h;
      lr.s = s;
      this.recompute(s);
      this.needsDraw = true;
    }
    for (let i = 0; i < this.vis.count; i++) c.want(this.vis.keys[i] ?? 0, PRIO_VISIBLE, frame);
    for (let i = 0; i < this.base.count; i++) c.want(this.base.keys[i] ?? 0, PRIO_BASE, frame);
    for (let i = 0; i < this.fallback.count; i++) c.want(this.fallback.keys[i] ?? 0, PRIO_BASE, frame);
    for (let i = 0; i < this.pre.count; i++) c.want(this.pre.keys[i] ?? 0, PRIO_PREFETCH, frame);
  }

  /** Runtime level bias (LITE: the battle cam one level coarser); re-picks the tile sets on the next collect. */
  setLevelBias(n: number): void {
    if (n === this.opts.levelBias) return;
    this.opts.levelBias = n;
    this.lastViewVersion = -1;
  }

  private recompute(pxScale: number): void {
    const grid = this.cache.grid;
    const v = this.view;
    rectCornersWorld(v, v.rect, this.quad);
    this.autoBias = 0;
    // the unbiased sets first (level at opts.levelBias, ring, base) ...
    this.level = levelForView(grid, v.mpp, pxScale, this.opts.levelBy, this.opts.levelBias);
    visibleTiles(grid, this.level, this.quad, this.vis, this.pre, this.opts.ring);
    if (this.level > 0) visibleTiles(grid, 0, this.quad, this.base, null, 0);
    else this.base.clear();
    // ... so the cache can grow for them BEFORE the budget loop: the bias below shrinks the set a
    // rule-based cache grows from, so without this a view larger than the 96 floor would stay
    // biased at a coarser level for good. The bias now applies only when even the 256 ceiling is
    // not enough, as before H1 (HPF2).
    this.cache.ensureCapacity(this.vis.count + this.base.count + this.pre.count);
    const budget = this.cache.capacity - this.opts.reserve;
    for (;;) {
      const bias = this.opts.levelBias + this.autoBias;
      this.level = levelForView(grid, v.mpp, pxScale, this.opts.levelBy, bias);
      visibleTiles(grid, this.level, this.quad, this.vis, this.pre, this.opts.ring);
      if (this.level > 0) visibleTiles(grid, 0, this.quad, this.base, null, 0);
      else this.base.clear();
      const need = this.vis.count + this.base.count;
      if (need + this.pre.count <= budget) break;
      if (need <= budget) {
        this.pre.clear(); // drop the prefetch ring before thrashing
        break;
      }
      if (this.level === 0) break;
      this.autoBias++;
    }
  }

  /** Phase 3: rebuild the instance buffers when the view or the cache changed. Allocation-free. */
  draw(frame: number): void {
    if (!this.enabled) return;
    const c = this.cache;
    if (!this.needsDraw && c.version === this.lastCacheVersion) return;
    this.needsDraw = false;
    this.lastCacheVersion = c.version;
    const grid = c.grid;
    for (const m of this.meshes) m.n = 0;
    this.fallback.clear();
    this.drawn = 0;
    const z = grid.levels[this.level]?.z ?? 0;
    for (let i = 0; i < this.vis.count; i++) {
      const k = this.vis.keys[i] ?? 0;
      if (c.get(k, frame)) {
        this.push(k);
        continue;
      }
      for (let zp = z - 1; zp >= 0; zp--) {
        const ak = ancestorKey(k, zp);
        if (c.isReady(ak)) {
          if (!this.fallback.has(ak)) {
            this.fallback.add(ak);
            this.push(ak);
          }
          break;
        }
      }
    }
    for (const m of this.meshes) {
      m.mesh.thinInstanceCount = m.n;
      if (m.n > 0) m.mesh.thinInstanceBufferUpdated('matrix');
      m.mesh.isVisible = m.n > 0;
    }
  }

  private push(key: number): void {
    const c = this.cache;
    const slot = c.slotOf(key);
    if (slot < 0) return;
    const m = this.meshes[Math.floor(slot / c.pageLayers)];
    if (!m) return;
    const grid = c.grid;
    const zi = keyZ(key);
    let tm = 1;
    for (let i = 0; i < grid.levels.length; i++) {
      const lv = grid.levels[i];
      if (lv && lv.z === zi) tm = lv.tile_m;
    }
    const o = m.n * 16;
    m.data[o] = grid.origin[0] + keyX(key) * tm;
    m.data[o + 1] = grid.origin[1] + keyY(key) * tm;
    m.data[o + 2] = tm;
    m.data[o + 3] = slot % c.pageLayers;
    m.n++;
    this.drawn++;
  }

  stats(): TileLayerStats {
    let draws = 0;
    for (const m of this.meshes) if (m.n > 0) draws++;
    return {
      level: this.cache.grid.levels[this.level]?.z ?? 0,
      autoBias: this.autoBias,
      visible: this.vis.count,
      prefetch: this.pre.count,
      drawn: this.drawn,
      fallback: this.fallback.count,
      draws,
    };
  }

  dispose(): void {
    if (this.bindObs) this.material.onBindObservable.remove(this.bindObs);
    for (const m of this.meshes) m.mesh.dispose(false, false);
    this.meshes.length = 0;
    this.material.dispose();
  }
}
